import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { format, isToday, nextDay } from 'date-fns';
import { useRoute } from '@vmos2/utils';
import { orderServerlessApi, storage } from '@vmos2/core';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import type { Node } from 'react';
import { useSettings } from './Settings';

export const StockQuantityContext = createContext({});

type Props = {
  isFutureDayOrdering: boolean,
  children: Node,
};

export const StockQuantityProvider = ({ isFutureDayOrdering, children }: Props): Node => {
  const { route } = useRoute();
  const [stockQuantity, setStockQuantity] = useState({});
  const [desiredOrderFulfilmentTime, setDesiredOrderFulfilmentTime] = useState({});
  const { stockManagementLimitedQuantity } = useSettings();

  useEffect(() => {
    (async () => {
      const value = await storage.getItem('desiredOrderFulfilmentTime');
      setDesiredOrderFulfilmentTime(value);
    })();
  }, []);

  const date =
    useMemo(() => {
      const isFutureDayOrderingAvailable = isFutureDayOrdering && get(stockManagementLimitedQuantity, 'ignoreFutureDayOrdering', false);
      const dateForDesiredOrderFulfilmentTime = isEmpty(desiredOrderFulfilmentTime) ? format(new Date(), 'dd-MM-yyyy') : format(nextDay(new Date(), desiredOrderFulfilmentTime.weekDay), 'dd-MM-yyyy');

      return !isFutureDayOrderingAvailable ? dateForDesiredOrderFulfilmentTime : null;
    }, [isFutureDayOrdering, stockManagementLimitedQuantity, desiredOrderFulfilmentTime]);

  useEffect(() => {
    if (!!route.params.storeUUID && get(stockManagementLimitedQuantity, 'enabled')) {
      orderServerlessApi
        .get('get-ordered-products-count', {
          params: {
            lifetime: true,
            date,
            extStoreUUID: route.params.storeUUID,
          },
        })
        .then(res => setStockQuantity(res));
    }
    return () => setStockQuantity({});
  }, [route.params.storeUUID, stockManagementLimitedQuantity, date]);

  const updateStockQuantity = useCallback(({ payload }) => {
    const { productUUID, productCount, date } = payload;
    const [day, month, year] = date.split('-');
    const isTodayDate = isToday(new Date(year, month - 1, day));
    setStockQuantity(res => ({
      ...res,
      [productUUID]: {
        ...res[productUUID],
        [isTodayDate ? 'dailyCount' : 'lifetimeCount']: productCount,
      }
    }));
  }, []);

  const providerValue = useMemo(() => ({ stockQuantity, updateStockQuantity }), [stockQuantity, updateStockQuantity]);

  return <StockQuantityContext.Provider value={providerValue}>{children}</StockQuantityContext.Provider>;
};

export const useStockQuantity = () => {
  return useContext(StockQuantityContext);
};
